// composer/ComposerCaptions.tsx — the attached clip's Captions row (1.27.60, owner ruling):
// add your own .srt/.vtt, or opt in to captions made automatically on this device (only when
// you brought no file). Automatic captions can be corrected before posting.
import { useRef, useState } from 'react';
import styled from 'styled-components';
import { Button, MetaRow } from '@/ui';
import { Icon } from '@/icons';
import { CAPTION_LANGUAGES } from '@/features/videos/captions/languages';
import type { ComposerCaptionsHook } from './useComposerCaptions';
import { CaptionReviewDialog } from './CaptionReviewDialog';

const Row = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
  font: 500 ${({ theme }) => theme.fontSize.sm} ${({ theme }) => theme.font.body};
  color: ${({ theme }) => theme.color.textPrimary};
  select {
    padding: 6px 8px;
    border-radius: ${({ theme }) => theme.radius.sm};
    border: 1px solid ${({ theme }) => theme.color.glassBorder};
    background: ${({ theme }) => theme.color.surface};
    color: ${({ theme }) => theme.color.textPrimary};
    font: inherit;
  }
  label.auto {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    cursor: pointer;
  }
`;

const Hint = styled.p<{ $bad?: boolean }>`
  margin: 0;
  flex-basis: 100%;
  font: 400 ${({ theme }) => theme.fontSize.xs} ${({ theme }) => theme.font.body};
  color: ${({ theme, $bad }) => ($bad ? theme.color.danger : theme.color.textMuted)};
`;

const Bar = styled.span`
  display: inline-block;
  width: 120px;
  height: 6px;
  border-radius: 3px;
  background: ${({ theme }) => theme.color.glassBorder};
  overflow: hidden;
  span {
    display: block;
    height: 100%;
    background: ${({ theme }) => theme.color.accentCyan};
    transition: width 200ms ease;
  }
`;

const STAGE: Record<string, string> = {
  reading: 'Reading the sound…',
  model: 'Getting the speech model ready (first time only)…',
  listening: 'Listening…',
};

export function ComposerCaptions({
  captions: hook,
  disabled,
}: {
  captions: ComposerCaptionsHook;
  disabled?: boolean;
}) {
  const fileRef = useRef<HTMLInputElement>(null);
  const [reviewing, setReviewing] = useState(false);
  const c = hook.captions;

  const language = (
    <select
      aria-label="Caption language"
      value={hook.language}
      disabled={disabled || c.kind === 'working'}
      onChange={(e) => hook.setLanguage(e.target.value)}
    >
      {CAPTION_LANGUAGES.map((l) => (
        <option key={l.code} value={l.code}>
          {l.label}
        </option>
      ))}
    </select>
  );

  return (
    <Row role="group" aria-label="Captions" data-testid="composer-captions">
      <Icon name="captions" size={16} />
      <input
        ref={fileRef}
        type="file"
        accept=".vtt,.srt,text/vtt"
        hidden
        aria-label="Caption file"
        onChange={(e) => {
          const file = e.target.files?.[0];
          e.target.value = '';
          if (file) void hook.pickFile(file);
        }}
      />
      {(c.kind === 'none' || c.kind === 'error') && (
        <>
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => fileRef.current?.click()}
          >
            Add a caption file
          </Button>
          <label className="auto">
            <input
              type="checkbox"
              checked={false}
              disabled={disabled}
              onChange={() => void hook.makeAutomatically()}
            />
            Make captions automatically
          </label>
          {language}
          {c.kind === 'error' ? (
            <Hint $bad role="alert">
              {c.message}
            </Hint>
          ) : (
            <Hint>
              .srt or .vtt. Automatic captions are made on this device, so the sound never leaves
              it; long videos take a while, and you can fix the words before posting.
            </Hint>
          )}
        </>
      )}
      {c.kind === 'file' && (
        <>
          <MetaRow>
            <span>{c.name}</span>
            <span>
              {c.cues.length} {c.cues.length === 1 ? 'line' : 'lines'}
            </span>
          </MetaRow>
          {language}
          <Button variant="ghost" size="sm" disabled={disabled} onClick={hook.clear}>
            Remove
          </Button>
        </>
      )}
      {c.kind === 'working' && (
        <>
          <span role="status">{STAGE[c.stage]}</span>
          <Bar aria-hidden="true">
            <span style={{ width: `${Math.round(c.fraction * 100)}%` }} />
          </Bar>
          <Button variant="ghost" size="sm" onClick={hook.clear}>
            Cancel
          </Button>
          <Hint>You can post once the captions are ready, or cancel them to post now.</Hint>
        </>
      )}
      {c.kind === 'auto' && (
        <>
          <span>
            {c.cues.length} {c.cues.length === 1 ? 'line' : 'lines'} made automatically
          </span>
          {language}
          <Button
            variant="secondary"
            size="sm"
            disabled={disabled}
            onClick={() => setReviewing(true)}
          >
            Review
          </Button>
          <Button variant="ghost" size="sm" disabled={disabled} onClick={hook.clear}>
            Remove
          </Button>
        </>
      )}
      {reviewing && c.kind === 'auto' && (
        <CaptionReviewDialog
          cues={c.cues}
          onSave={hook.setCues}
          onClose={() => setReviewing(false)}
        />
      )}
    </Row>
  );
}
